/**
 * TITAM AI - Componentes de Filtro
 * Filtros para Google Ads: Período e Contas
 */

import { useState, useRef, useEffect } from 'react'
import Icon from './icons.jsx'
import { useApp } from '../context/AppContext.jsx'
import { PeriodPicker } from './UI.jsx'

/**
 * PeriodFilter - Seletor de período com inputs de data
 */
function PeriodFilter({ start, end, onChange, compact = false }) {
  return (
    <div className={`filter-date-group ${compact ? 'compact' : ''}`}>
      <div className="filter-date">
        {!compact && <label>Data início</label>}
        <input
          type="date"
          value={start}
          onChange={(e) => onChange({ start: e.target.value, end })}
        />
      </div>
      <span className="filter-date-separator">→</span>
      <div className="filter-date">
        {!compact && <label>Data fim</label>}
        <input
          type="date"
          value={end}
          onChange={(e) => onChange({ start, end: e.target.value })}
        />
      </div>
    </div>
  )
}

/**
 * AccountFilter - Seletor multiplo de contas
 */
export function AccountFilter({ accounts, selected, onChange, compact = false }) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleToggle = (accountId) => {
    if (selected.includes(accountId)) {
      onChange(selected.filter(id => id !== accountId))
    } else {
      onChange([...selected, accountId])
    }
  }

  const handleSelectAll = () => {
    if (selected.length === accounts.length) {
      onChange([])
    } else {
      onChange(accounts.map(a => a.id))
    }
  }

  const selectedCount = selected.length
  const buttonText = selectedCount === 0
    ? 'Todas as contas'
    : selectedCount === 1
      ? accounts.find(a => a.id === selected[0])?.name || '1 conta'
      : `${selectedCount} contas`

  if (compact) {
    return (
      <div className="filter-group" ref={dropdownRef}>
        <div className="account-selector">
          <button
            className={`account-selector-btn ${isOpen ? 'active' : ''}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <span className={selectedCount > 0 ? 'has-selection' : ''}>
              {buttonText}
            </span>
            <Icon n={isOpen ? 'chevron_u' : 'chevron_d'} s={14} />
          </button>
          {isOpen && (
            <div className="account-selector-dropdown">
              {accounts.map(account => (
                <div
                  key={account.id}
                  className="account-option"
                  onClick={() => handleToggle(account.id)}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(account.id)}
                    onChange={() => {}}
                  />
                  <label>{account.name}</label>
                </div>
              ))}
              <div className="account-selector-footer">
                <button onClick={handleSelectAll}>
                  {selectedCount === accounts.length ? 'Desmarcar todas' : 'Selecionar todas'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="filter-group" ref={dropdownRef}>
      <label>Contas Google Ads</label>
      <div className="account-selector">
        <button
          className={`account-selector-btn ${isOpen ? 'active' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className={selectedCount > 0 ? 'has-selection' : ''}>
            {buttonText}
          </span>
          <Icon n={isOpen ? 'chevron_u' : 'chevron_d'} s={14} />
        </button>
        {isOpen && (
          <div className="account-selector-dropdown">
            {accounts.map(account => (
              <div
                key={account.id}
                className="account-option"
                onClick={() => handleToggle(account.id)}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(account.id)}
                  onChange={() => {}}
                />
                <label>{account.name}</label>
              </div>
            ))}
            <div className="account-selector-footer">
              <button onClick={() => onChange([])}>Limpar</button>
              <button className="primary" onClick={handleSelectAll}>
                {selectedCount === accounts.length ? 'Desmarcar todas' : 'Selecionar todas'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * GoogleAdsFilters - Barra completa de filtros para Google Ads
 */
function GoogleAdsFilters({ onApply, compact = false }) {
  const { accounts, period, setPeriod, selectedAccounts, setSelectedAccounts } = useApp()
  const [localPeriod, setLocalPeriod] = useState(period)
  const [localAccounts, setLocalAccounts] = useState(selectedAccounts)

  const handleApply = () => {
    setPeriod(localPeriod)
    setSelectedAccounts(localAccounts)
    onApply?.({ period: localPeriod, accounts: localAccounts })
  }

  const handleClear = () => {
    setLocalPeriod({ start: '', end: '' })
    setLocalAccounts([])
  }

  if (compact) {
    return (
      <div className="filters-bar compact">
        <PeriodFilter
          start={localPeriod.start}
          end={localPeriod.end}
          onChange={setLocalPeriod}
          compact
        />
        <AccountFilter
          accounts={accounts}
          selected={localAccounts}
          onChange={setLocalAccounts}
          compact
        />
        <div className="filter-actions">
          <button className="filter-btn filter-btn-primary" onClick={handleApply}>
            <Icon n="search" s={14} />
            Aplicar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="filters-bar">
      <PeriodFilter
        start={localPeriod.start}
        end={localPeriod.end}
        onChange={setLocalPeriod}
      />
      <AccountFilter
        accounts={accounts}
        selected={localAccounts}
        onChange={setLocalAccounts}
      />
      <div className="filter-actions">
        <button className="filter-btn filter-btn-secondary" onClick={handleClear}>
          Limpar
        </button>
        <button className="filter-btn filter-btn-primary" onClick={handleApply}>
          <Icon n="search" s={14} />
          Aplicar Filtros
        </button>
      </div>
    </div>
  )
}

export function LeadsFilters({ filters, onChange, onApply, onClear }) {
  const { accounts } = useApp()
  const [local, setLocal] = useState({
    startDate: filters?.startDate || '',
    endDate: filters?.endDate || '',
    account_id: filters?.account_id || '',
  })

  useEffect(() => {
    setLocal({
      startDate: filters?.startDate || '',
      endDate: filters?.endDate || '',
      account_id: filters?.account_id || '',
    })
  }, [filters])

  const setField = (key, value) => {
    setLocal((prev) => ({ ...prev, [key]: value }))
  }

  const handleApply = () => {
    onChange?.(local)
    onApply?.(local)
  }

  const handleClear = () => {
    const empty = { startDate: '', endDate: '', account_id: '' }
    setLocal(empty)
    onChange?.(empty)
    onClear?.()
  }

  const hasFilters = local.startDate || local.endDate || local.account_id

  return (
    <div className="leads-filters-bar">
      <div className="lf-group">
        <label className="lf-label">Data de criacao</label>
        <PeriodPicker
          openRight
          value={{ start: local.startDate, end: local.endDate }}
          onChange={({ start, end }) =>
            setLocal((prev) => ({ ...prev, startDate: start, endDate: end }))
          }
        />
      </div>

      {accounts?.length > 1 ? (
        <div className="lf-group">
          <label className="lf-label">Conta</label>
          <div className="lf-select-wrap">
            <select
              className="lf-select"
              value={local.account_id}
              onChange={(e) => setField('account_id', e.target.value)}
            >
              <option value="">Todas as contas</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
            <Icon n="chevron_d" s={12} />
          </div>
        </div>
      ) : null}

      <div className="lf-actions">
        {hasFilters ? (
          <button className="lf-btn lf-btn-ghost" onClick={handleClear}>
            Limpar
          </button>
        ) : null}
        <button className="lf-btn lf-btn-primary" onClick={handleApply}>
          <Icon n="search" s={13} />
          Aplicar
        </button>
      </div>
    </div>
  )
}

